import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  Grid,
  Paper,
  Avatar,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import { fetchReport, handleError, formatDateForMySQL, formatDate2 } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import dayjs from 'dayjs';
import { useReportDatePickerLocale } from '../../hooks/useReportDatePickerLocale';

const LogItem = ({ log }) => {
  const { t } = useTranslation();
  const getOperationColor = (operation) => {
    switch (operation.toLowerCase()) {
      case 'created':
        return 'success';
      case 'deleted':
        return 'error';
      case 'updated':
        return 'primary';
      case 'readed':
        return 'info';
      default:
        return 'default';
    }
  };



  return (
    <Paper 
      sx={{ 
        p: 2, 
        mb: 2, 
        display: 'flex', 
        alignItems: 'center',
        '&:hover': { 
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          cursor: 'default'
        }
      }}
    >
      <Avatar 
        src={log.user?.thumb_url} 
        sx={{ width: 48, height: 48, mr: 2 }}
      >
        {!log.user?.thumb_url && <PersonIcon />}
      </Avatar>
      
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 2 }}>
            {log.user?.name || t('reports.unknownUser')}
          </Typography>
          <Chip 
            label={log.operation} 
            color={getOperationColor(log.operation)}
            size="small"
            sx={{ mr: 1 }}
          />
          <Chip 
            label={log.table_name} 
            variant="outlined"
            size="small"
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          {formatDate2(log.created_at, true)}
        </Typography>
      </Box>
    </Paper>
  );
};

const LogsReport = () => {
  const { t } = useTranslation();
  const { datePickerFormat, dayjsPickerLocale } = useReportDatePickerLocale();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  const { setNavigation, state } = useNavigationHelperStore();
  
  // Date filters
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  
  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { startDate: savedStartDate, endDate: savedEndDate, page: savedPage } = state;
      if (savedStartDate) setStartDate(dayjs(savedStartDate));
      if (savedEndDate) setEndDate(dayjs(savedEndDate));
      if (savedPage) setPage(savedPage);
      handleSearch();
    } else {
      // Initial load
      handleSearch();
    }
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      page
    });
  };

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const params = {
        from: formattedStartDate,
        to: formattedEndDate
      };
      
      const response = await fetchReport('LOGS', 1, params);
      setData(response);
      setPage(1);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = async (event, value) => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const params = {
        from: formattedStartDate,
        to: formattedEndDate
      };
      
      const response = await fetchReport('LOGS', value, params);
      setData(response);
      setPage(value);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.from')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format={datePickerFormat}
                slotProps={{
                  textField: { fullWidth: true, variant: 'outlined' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.to')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format={datePickerFormat}
                minDate={startDate}
                slotProps={{
                  textField: { fullWidth: true, variant: 'outlined' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <SearchIcon 
                onClick={handleSearch}
                sx={{ 
                  fontSize: 28, 
                  cursor: 'pointer',
                  color: isLoading ? 'text.disabled' : 'text.primary',
                  display: 'block',
                  margin: 'auto'
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <HistoryIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">
                {t('reports.logsTitle')} ({data.total_results || (data.content ? data.content.length : 0)})
              </Typography>
            </Box>
          </Box>
          
          {data.content && data.content.length > 0 ? (
            <>
              {data.content.map((log, index) => (
                <LogItem key={`${log.user?.id}-${log.created_at}-${index}`} log={log} />
              ))}
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3} component={Paper} variant="outlined">
              <HistoryIcon color="action" sx={{ fontSize: 40, mb: 2, opacity: 0.6 }} />
              <Typography variant="body1">{t('reports.noLogs')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.selectDateRange')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default LogsReport; 